import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  TextField,
  Fade,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Portal,
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import {
  Search,
  X,
  Plus,
  Minimize2,
  Maximize2,
  Trash2,
  FolderSymlink,
} from 'lucide-react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción
import CategorizedChatList from '../list/CategorizedChatList';
import CategoryManager from '../categories/CategoryManager';
import ProjectManager from '../projects/ProjectManager';
import { ProjectContextProvider } from '../projects/ProjectContextProvider';

// Helper function for colors - Apple palette
const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const appleColors = {
    light: {
      label: { primary: '#000000' },
      system: {
        grey: '#8E8E93',
        grey2: '#AEAEB2',
        grey3: '#C7C7CC',
        grey6: '#F2F2F7',
        inputBg: '#F2F2F7',
        green: '#34C759',
        white: '#FFFFFF',
        black: '#000000'
      },
      bg: { 
        primaryElevated: '#FFFFFF',
        secondary: '#F2F2F7',
        tertiary: '#FAFAFA'
      },
      fill: { tertiary: '#767680' },
      ui: { accent: '#007AFF', base: '#F2F2F7' }
    },
    dark: {
      label: { primary: '#FFFFFF' },
      system: {
        grey: '#8E8E93',
        grey2: '#636366',
        grey3: '#464649',
        grey6: '#1C1C1E',
        inputBg: '#1C1C1E',
        green: '#30D158',
        white: '#FFFFFF',
        black: '#000000'
      },
      bg: { 
        primaryElevated: '#1C1C1E',
        secondary: '#2C2C2E',
        tertiary: '#3A3A3C'
      },
      fill: { tertiary: '#767680' },
      ui: { accent: '#0A84FF', base: '#3A3A3C' }
    }
  };
  
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: alpha(c.system.grey3, 0.3),
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// Styled components for the modal
const BlurBackdrop = ({ open, onClick }) => {
  const theme = useTheme();

  return (
    <Portal>
      <Box
        onClick={onClick}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha('#000000', 0.14),
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          zIndex: theme.zIndex.modal - 1,
          opacity: open ? 1 : 0,
          transition: 'opacity 300ms ease',
          visibility: open ? 'visible' : 'hidden',
        }}
      />
    </Portal>
  );
};

const ModalContainer = styled(Box, {
  shouldForwardProp: (prop) => !['isMinimized', 'isBlurred'].includes(prop),
})(({ theme, isMinimized, isBlurred }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMinimized ? '350px' : '90%',
    maxWidth: isMinimized ? '350px' : '900px',
    height: isMinimized ? '65vh' : '85vh',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '20px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.background,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : 'none',
    overflow: 'hidden',
    outline: 'none',
    filter: isBlurred ? 'blur(2px)' : 'none',
    pointerEvents: isBlurred ? 'none' : 'auto',
    opacity: isBlurred ? 0.65 : 1,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 10px 25px rgba(0, 0, 0, 0.4)'
      : '0 10px 25px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundColor: isBlurred ? alpha('#000000', 0.25) : 'transparent',
      pointerEvents: 'none',
      transition: 'background-color 0.3s ease',
    },
    '& *::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '& *::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '& *::-webkit-scrollbar-thumb': {
      background: alpha(colors.textSecondary, 0.3),
      borderRadius: '6px',
    },
    '& *::-webkit-scrollbar-thumb:hover': {
      background: alpha(colors.textSecondary, 0.5),
    },
    '& *::-webkit-scrollbar-button': {
      display: 'none',
    },
  };
});

const SearchHeader = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    padding: '16px 20px',
    borderBottom: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.borderColor}`,
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : colors.background,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };
});

const SearchInputWrapper = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.1)'
      : (theme.palette.mode === 'dark' ? 'rgba(28, 28, 30, 0.8)' : 'rgba(242, 242, 247, 0.8)'),
    borderRadius: '10px',
    padding: '8px 12px',
  };
});

const ContentContainer = styled(Box)({
  display: 'flex',
  flexGrow: 1,
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
});

const SidebarSection = styled(Box)(({ theme, isMinimized }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: isMinimized ? '100%' : '300px',
    minWidth: isMinimized ? '100%' : '300px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: isMinimized ? 'none' : `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.borderColor}`,
    padding: theme.spacing(2),
    overflow: 'hidden',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : colors.background,
    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',

    '& .MuiListItemButton-root, & .MuiListItem-root': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      }
    },

    '& [data-component="CategoryManager"] .MuiListItemButton-root': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      }
    },

    '& [data-component="ProjectManager"] .MuiListItemButton-root': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      }
    }
  };
});

const MainContent = styled(Box)(({ theme, isMinimized }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexGrow: 1,
    flexShrink: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : colors.background,
    overflow: 'hidden',
    opacity: isMinimized ? 0 : 1,
    width: isMinimized ? 0 : 'auto',
    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
    '& .MuiListItemButton-root, & .MuiListItem-root': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      }
    }
  };
});

const ContentHeader = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.borderColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : colors.background,
  };
});

const HeaderTitle = styled(Typography)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontWeight: 600,
    fontSize: '18px',
    color: isWallpaperMode ? '#FFFFFF' : colors.textDefault,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -5,
      left: 0,
      width: '40px',
      height: '3px',
      borderRadius: '1.5px',
      backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
    }
  };
});

const ContentBody = styled(Box)({
  flexGrow: 1,
  overflow: 'auto',
  padding: '20px',
});

const ModalFooter = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    padding: '12px 16px',
    borderTop: `1px solid ${isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.borderColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : colors.background,
  };
});

const NavigationSection = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  
  return {
    marginBottom: theme.spacing(3),
    backgroundColor: 'transparent',
    '& .MuiListItemButton-root, & .MuiListItem-root': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      }
    },
    '& .MuiList-root .MuiListItemButton-root': {
      backgroundColor: 'transparent !important',
      '&:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      },
      '&.Mui-selected': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(colors.textDefault, 0.05) + ' !important',
      }
    }
  };
});

const ActionButton = styled(IconButton)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    color: colors.textDefault,
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: alpha(colors.textDefault, 0.05),
    },
    transition: 'all 0.25s ease',
  };
});

const BlueButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? '#FFFFFF'
      : (theme.palette.mode === 'dark' ? '#0A84FF' : '#007AFF'),
    color: isWallpaperMode
      ? '#000000'
      : 'white',
    padding: '8px',
    minWidth: '34px',
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.85)'
        : (theme.palette.mode === 'dark' ? '#0071E3' : '#0062CC'),
    },
    '& svg': {
      width: '18px',
      height: '18px',
    },
    transition: 'all 0.25s ease',
  };
});

// Styled components for the category change dialog
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontWeight: 600,
    fontSize: '18px',
    color: isWallpaperMode ? '#FFFFFF' : colors.textDefault,
    padding: '20px 24px 10px',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 24,
      width: '40px',
      height: '3px',
      borderRadius: '1.5px',
      backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
    }
  };
});

const StyledDialogContent = styled(DialogContent)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    padding: '24px',
    backgroundColor: colors.background,
  };
});

const StyledDialogActions = styled(DialogActions)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    padding: '16px 24px 20px',
    backgroundColor: colors.background,
    borderTop: `1px solid ${colors.borderColor}`,
  };
});

const StyledSelect = styled(Select)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    borderRadius: '10px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.borderColor,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.accent,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.accent,
    },
    '& .MuiSelect-select': {
      padding: '12px 14px',
    }
  };
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => {
  const colors = navigationColors(theme);
  return {
    fontSize: '15px',
    padding: '10px 16px',
    borderRadius: '8px',
    margin: '2px 0',
    '&:hover': {
      backgroundColor: alpha(colors.accent, 0.08),
    },
    '&.Mui-selected': {
      backgroundColor: alpha(colors.accent, 0.12),
      '&:hover': {
        backgroundColor: alpha(colors.accent, 0.16),
      }
    }
  };
});

const StyledButton = styled(Button)(({ theme, color = 'primary' }) => {
  const colors = navigationColors(theme);
  const isCancel = color === 'inherit';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '10px',
    padding: '8px 16px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    minWidth: '80px',
    boxShadow: 'none',
    ...(isCancel ? {
      borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.3)' : colors.borderColor,
      color: isWallpaperMode ? '#FFFFFF' : colors.textDefault,
      '&:hover': {
        borderColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.5)' : colors.textSecondary,
        backgroundColor: 'transparent',
      }
    } : {
      backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.accent,
      color: isWallpaperMode ? '#000000' : '#ffffff',
      '&:hover': {
        backgroundColor: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.85)'
          : (theme.palette.mode === 'dark' ? '#0071E3' : '#0062CC'),
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(isWallpaperMode ? '#FFFFFF' : colors.accent, 0.5),
        color: isWallpaperMode ? '#000000' : '#ffffff',
      }
    })
  };
});

const ChatModal = ({
  isOpen,
  onClose,
  user,
  createNewChatHandler,
  searchTerm,
  setSearchTerm,
  isSearchFocused,
  setIsSearchFocused,
  showMoreFilters,
  setShowMoreFilters,
  selectedFilters,
  handleFilterToggle,
  clearAllFilters,
  filterCategories,
  selectedCategory,
  setSelectedCategory,
  selectedProject,
  currentChatId,
  filteredChats,
  selectChat,
  handleMenuOpen: externalHandleMenuOpen,
  hasNextPage,
  isNextPageLoading,
  loadChats,
  handleSelectProject,
  handleProjectChatSelect,
  persistentChatState,
  setPersistentChatState,
  projectsSectionRef,
  setSnackbarMessage,
  setSnackbarOpen,
  handleDeleteChat,
  updateChatCategory,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Hook de traducción
  const [isMinimized, setIsMinimized] = useState(false);
  
  // State for menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  
  // State for category change dialog
  const [changeCategoryDialogOpen, setChangeCategoryDialogOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isNestedModalVisible, setIsNestedModalVisible] = useState(false);
  const nestedModalCloseTimerRef = useRef(null);

  const isNestedModalOpen = isCategoryModalOpen || isProjectModalOpen;

  useEffect(() => {
    if (nestedModalCloseTimerRef.current) {
      clearTimeout(nestedModalCloseTimerRef.current);
      nestedModalCloseTimerRef.current = null;
    }

    if (isNestedModalOpen) {
      setIsNestedModalVisible(true);
      return undefined;
    }

    nestedModalCloseTimerRef.current = setTimeout(() => {
      setIsNestedModalVisible(false);
      nestedModalCloseTimerRef.current = null;
    }, 160);

    return () => {
      if (nestedModalCloseTimerRef.current) {
        clearTimeout(nestedModalCloseTimerRef.current);
        nestedModalCloseTimerRef.current = null;
      }
    };
  }, [isNestedModalOpen]);

  // Handle minimize/maximize with smooth transition
  const handleToggleSize = () => {
    // Force a reflow to make sure the transition happens
    // Using void to avoid the eslint warning
    void document.body.offsetHeight;
   
    setIsMinimized(!isMinimized);
  };

  // Menu handlers
  const handleMenuOpen = useCallback((event, chatId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedChatId(chatId);
   
    // Call external handler if provided
    if (externalHandleMenuOpen) {
      externalHandleMenuOpen(event, chatId);
    }
  }, [externalHandleMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleOpenChangeCategoryDialog = useCallback(() => {
    // Obtener categorías disponibles
    const categories = ['personal', 'work', 'team', 'ideas', 'uncategorized'];
   
    // Añadir categorías personalizadas si existen
    if (filterCategories && Array.isArray(filterCategories)) {
      filterCategories.forEach(cat => {
        if (!categories.includes(cat)) {
          categories.push(cat);
        }
      });
    }
   
    setAvailableCategories(categories);
    setChangeCategoryDialogOpen(true);
    handleMenuClose();
  }, [filterCategories, handleMenuClose]);

  const handleChangeCategory = useCallback(() => {
    if (!selectedChatId) {
      setSnackbarMessage(t('chatModal.errorNoSelection'));
      setSnackbarOpen(true);
      setChangeCategoryDialogOpen(false);
      setNewCategory('');
      return;
    }
   
    if (selectedChatId && newCategory) {
      // Primero, actualizar la categoría en el servidor
      updateChatCategory(selectedChatId, newCategory)
        .then((result) => {
          if (result.success) {
            setSnackbarMessage(t('chatModal.categoryChanged', { category: newCategory }));
            setSnackbarOpen(true);
   
            // Recargar la lista de chats
            loadChats(false)
              .catch(err => {
                setSnackbarMessage(t('chatModal.errorLoadingChats'));
                setSnackbarOpen(true);
              });
          } else {
            setSnackbarMessage(result.message || t('chatModal.failedToChangeCategory'));
            setSnackbarOpen(true);
          }
        })
        .catch((error) => {
          setSnackbarMessage(t('chatModal.failedToChangeCategory'));
          setSnackbarOpen(true);
        });
    }
    setChangeCategoryDialogOpen(false);
    setNewCategory('');
  }, [selectedChatId, newCategory, loadChats, setSnackbarMessage, setSnackbarOpen, updateChatCategory, t]);

  const handleDeleteSelectedChat = useCallback(() => {
    if (selectedChatId && handleDeleteChat) {
      handleDeleteChat(selectedChatId)
        .then((result) => {
          if (result.success) {
            setSnackbarMessage(t('chatModal.chatDeleted'));
            setSnackbarOpen(true);
          } else {
            setSnackbarMessage(result.message || t('chatModal.failedToDeleteChat'));
            setSnackbarOpen(true);
          }
        })
        .catch(() => {
          setSnackbarMessage(t('chatModal.errorDeletingChat'));
          setSnackbarOpen(true);
        });
    }
    handleMenuClose();
  }, [selectedChatId, handleDeleteChat, setSnackbarMessage, setSnackbarOpen, handleMenuClose, t]);

  // Debounced search function usando useMemo para evitar el warning
  const debouncedSearch = useMemo(() => 
    debounce(async (term) => {
      if (!user?.uid) return;
      
      if (!term.trim()) {
        loadChats(false);
        return;
      }
      
      loadChats(false);
    }, 300),
    [user?.uid, loadChats]
  );
  
  const handleSearchChange = useCallback((e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.trim()) {
      setIsSearchFocused(true);
    } else {
      setIsSearchFocused(false);
    }
    
    debouncedSearch(term);
  }, [debouncedSearch, setSearchTerm, setIsSearchFocused]);

  // Format category title for display
  const formatCategoryTitle = (category) => {
    if (!category) return t("chatModal.allChats");
    if (category === "uncategorized") return t("chatModal.uncategorized");
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Format category name for display in select menu
  const formatCategoryName = (category) => {
    if (category === "uncategorized") return t("chatModal.uncategorized");
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <>
      <BlurBackdrop open={isOpen} onClick={onClose} />
      
      <Modal
        open={isOpen}
        onClose={onClose}
        closeAfterTransition
        BackdropProps={{
          invisible: true,
        }}
        keepMounted
      >
        <Fade in={isOpen} timeout={300}>
          <ModalContainer isMinimized={isMinimized} isBlurred={isNestedModalVisible}>
            {/* Search Header */}
            <SearchHeader>
              <SearchInputWrapper>
                <Search size={16} color={theme.palette.text.secondary} style={{ marginRight: '8px' }} />
                <TextField
                  variant="standard"
                  placeholder={t("chatModal.searchChats")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{
                    flex: 1,
                    '& input': {
                      padding: 0,
                      fontSize: '15px',
                    }
                  }}
                />
                {searchTerm && (
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setSearchTerm('');
                      setIsSearchFocused(false);
                      loadChats(false);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <X size={14} />
                  </IconButton>
                )}
              </SearchInputWrapper>
            
              <BlueButton onClick={() => {
                createNewChatHandler();
                onClose();
              }}>
                <Plus />
              </BlueButton>
            </SearchHeader>

            {/* Main Content Container */}
            <ContentContainer>
              {/* Sidebar */}
              <SidebarSection isMinimized={isMinimized}>
                {/* Categories section - removed the title */}
                <NavigationSection>
                  <CategoryManager
                    data-component="CategoryManager"
                    userId={user?.uid}
                    selectedCategory={selectedCategory}
                    onCategoryChange={(category) => {
                      setSelectedCategory(category);
                      if (category !== 'add') {
                        handleSelectProject(null);
                      }
                    }}
                    onNotification={(notification) => {
                      setSnackbarMessage(notification.message);
                      setSnackbarOpen(true);
                    }}
                    onModalOpenChange={setIsCategoryModalOpen}
                  />
                </NavigationSection>

                {/* Projects section - removed the title */}
                <NavigationSection>
                  <ProjectContextProvider>
                    <ProjectManager
                      data-component="ProjectManager"
                      ref={projectsSectionRef}
                      selectedCategory={selectedCategory}
                      selectedProject={selectedProject}
                      onSelectProject={handleSelectProject}
                      user={user}
                      onProjectChatSelect={handleProjectChatSelect}
                      onModalOpenChange={setIsProjectModalOpen}
                    />
                  </ProjectContextProvider>
                </NavigationSection>

                {/* In minimized mode, we show chats in the sidebar - removed the title */}
                {isMinimized && (
                  <NavigationSection sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <ProjectContextProvider>
                      <CategorizedChatList
                        data-component="CategorizedChatList"
                        chats={filteredChats}
                        currentChatId={currentChatId}
                        onSelectChat={(chat) => {
                          selectChat(chat);
                          onClose();
                        }}
                        onMenuOpen={handleMenuOpen}
                        hasNextPage={hasNextPage}
                        isNextPageLoading={isNextPageLoading}
                        onLoadMore={() => loadChats(true)}
                        selectedCategory={selectedCategory}
                        persistentState={persistentChatState}
                        onPersistentStateChange={(newState) => {
                          setPersistentChatState((prev) => ({
                            ...prev,
                            ...newState,
                          }));
                        }}
                        onCreateNewChat={() => {
                          createNewChatHandler();
                          onClose();
                        }}
                      />
                    </ProjectContextProvider>
                  </NavigationSection>
                )}
              </SidebarSection>

              {/* Main Content - Hide but keep in DOM while transitioning */}
              <MainContent isMinimized={isMinimized}>
                <ContentHeader>
                  <HeaderTitle>
                    {formatCategoryTitle(selectedCategory)}
                  </HeaderTitle>
                </ContentHeader>

                <ContentBody>
                  <ProjectContextProvider>
                    <CategorizedChatList
                      data-component="CategorizedChatList"
                      chats={filteredChats}
                      currentChatId={currentChatId}
                      onSelectChat={(chat) => {
                        selectChat(chat);
                        onClose();
                      }}
                      onMenuOpen={handleMenuOpen}
                      hasNextPage={hasNextPage}
                      isNextPageLoading={isNextPageLoading}
                      onLoadMore={() => loadChats(true)}
                      selectedCategory={selectedCategory}
                      persistentState={persistentChatState}
                      onPersistentStateChange={(newState) => {
                        setPersistentChatState((prev) => ({
                          ...prev,
                          ...newState,
                        }));
                      }}
                      onCreateNewChat={() => {
                        createNewChatHandler();
                        onClose();
                      }}
                    />
                  </ProjectContextProvider>
                </ContentBody>
              </MainContent>
            </ContentContainer>

            {/* Footer */}
            <ModalFooter>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
                {t("chatModal.chatsIn", { count: filteredChats?.length || 0, category: selectedCategory ? formatCategoryTitle(selectedCategory).toLowerCase() : t("chatModal.allCategories") })}
              </Typography>
              
              <ActionButton onClick={handleToggleSize}>
                {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
              </ActionButton>
            </ModalFooter>
          </ModalContainer>
        </Fade>
      </Modal>
      
      {/* Chat Action Menu - Con la nueva opción de cambiar categoría */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: '180px',
            borderRadius: '12px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 4px 20px rgba(0, 0, 0, 0.5)' 
              : '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Nueva opción para cambiar categoría */}
        <MenuItem 
          onClick={handleOpenChangeCategoryDialog}
          sx={{ 
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
            }
          }}
        >
          <FolderSymlink 
            size={16} 
            style={{ 
              marginRight: '10px',
              color: theme.palette.primary.main
            }} 
          />
          <Typography 
            sx={{ 
              fontSize: '14px',
              color: theme.palette.text.primary
            }}
          >
            {t("chatModal.changeCategory")}
          </Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={handleDeleteSelectedChat}
          sx={{ 
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.1),
            }
          }}
        >
          <Trash2 
            size={16} 
            style={{ 
              marginRight: '10px',
              color: theme.palette.error.main
            }} 
          />
          <Typography 
            sx={{ 
              fontSize: '14px',
              color: theme.palette.error.main
            }}
          >
            {t("chatModal.deleteChat")}
          </Typography>
        </MenuItem>
      </Menu>
      
      {/* Diálogo para cambiar categoría - Rediseñado para coincidir con el estilo del modal principal */}
      <Dialog 
        open={changeCategoryDialogOpen} 
        onClose={() => setChangeCategoryDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            width: '100%',
            maxWidth: '400px',
            overflow: 'hidden',
            backgroundColor: theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 10px 25px rgba(0, 0, 0, 0.4)' 
              : '0 10px 25px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <StyledDialogTitle>
          {t("chatModal.moveChat")}
        </StyledDialogTitle>
        
        <StyledDialogContent>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="category-select-label" sx={{ 
              fontSize: '15px',
              color: theme.palette.text.secondary,
            }}>
              {t("chatModal.selectCategory")}
            </InputLabel>
            <StyledSelect
              labelId="category-select-label"
              id="category-select"
              value={newCategory}
              label={t("chatModal.selectCategory")}
              onChange={(e) => setNewCategory(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    mt: 1,
                    padding: '6px',
                    maxHeight: '300px',
                    boxShadow: theme.palette.mode === 'dark' 
                      ? '0 4px 20px rgba(0, 0, 0, 0.5)' 
                      : '0 4px 20px rgba(0, 0, 0, 0.15)',
                  }
                }
              }}
            >
              {availableCategories.map((category) => (
                <StyledMenuItem key={category} value={category}>
                  {formatCategoryName(category)}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </StyledDialogContent>
        
        <StyledDialogActions>
          <StyledButton 
            onClick={() => setChangeCategoryDialogOpen(false)} 
            color="inherit"
            variant="outlined"
          >
            {t("settings.cancel")}
          </StyledButton>
          <StyledButton 
            onClick={handleChangeCategory} 
            variant="contained"
            disabled={!newCategory}
          >
            {t("chatModal.move")}
          </StyledButton>
        </StyledDialogActions>
      </Dialog>
    </>
  );
};

export default ChatModal;
